import type { Application } from '@feathersjs/feathers'
import { nanoid } from 'nanoid'
import type {
  ClientProducts,
  ClientProductsData,
  ClientProductsPatch,
  ClientProductsQuery,
} from '../types/typebox/products.schema'
import type {
  ClientApps,
  ClientAppsData,
  ClientAppsPatch,
  ClientAppsQuery,
} from '../types/typebox/apps.schema'
import type {
  ClientUsers,
  ClientUsersData,
  ClientUsersPatch,
  ClientUsersQuery,
} from '../types/typebox/users.schema'
import type {
  ClientStorefronts,
  ClientStorefrontsData,
  ClientStorefrontsPatch,
  ClientStorefrontsQuery,
} from '../types/typebox/storefronts.schema'
import type {
  ClientGoods,
  ClientGoodsData,
  ClientGoodsPatch,
  ClientGoodsQuery,
} from '../types/typebox/goods.schema'
import type {
  ClientTransactions,
  ClientTransactionsData,
  ClientTransactionsPatch,
  ClientTransactionsQuery,
} from '../types/typebox/transactions.schema'
import type {
  ClientStockTransactions,
  ClientStockTransactionsData,
  ClientStockTransactionsPatch,
  ClientStockTransactionsQuery,
} from '../types/typebox/stock-transactions.schema'
import type {
  ClientMenuItems,
  ClientMenuItemsData,
  ClientMenuItemsPatch,
  ClientMenuItemsQuery,
} from '../types/typebox/menu-items.schema'
import type {
  ClientOrders,
  ClientOrdersData,
  ClientOrdersPatch,
  ClientOrdersQuery,
} from '../types/typebox/orders.schema'
import type {
  ClientStorefrontPlaces,
  ClientStorefrontPlacesData,
  ClientStorefrontPlacesPatch,
  ClientStorefrontPlacesQuery,
} from '../types/typebox/storefront-places.schema'
import type {
  ClientMenus,
  ClientMenusData,
  ClientMenusPatch,
  ClientMenusQuery,
} from '../types/typebox/menus.schema'
import type {
  ClientShifts,
  ClientShiftsData,
  ClientShiftsPatch,
  ClientShiftsQuery,
} from '../types/typebox/shifts.schema'
import type {
  ClientShiftTemplates,
  ClientShiftTemplatesData,
  ClientShiftTemplatesPatch,
  ClientShiftTemplatesQuery,
} from '../types/typebox/shift-templates.schema'
import type {
  ClientStaffContracts,
  ClientStaffContractsData,
  ClientStaffContractsPatch,
  ClientStaffContractsQuery,
} from '../types/typebox/staff-contracts.schema'
import type {
  ClientStaffPayroll,
  ClientStaffPayrollData,
  ClientStaffPayrollPatch,
  ClientStaffPayrollQuery,
} from '../types/typebox/staff-payroll.schema'
import type {
  ClientTasks,
  ClientTasksData,
  ClientTasksPatch,
  ClientTasksQuery,
} from '../types/typebox/tasks.schema'
import type {
  ClientTaskLogs,
  ClientTaskLogsData,
  ClientTaskLogsPatch,
  ClientTaskLogsQuery,
} from '../types/typebox/task-logs.schema'
import type {
  ClientPromotions,
  ClientPromotionsData,
  ClientPromotionsPatch,
  ClientPromotionsQuery,
} from '../types/typebox/promotions.schema'
import type {
  ClientProcurement,
  ClientProcurementData,
  ClientProcurementPatch,
  ClientProcurementQuery,
} from '../types/typebox/procurement.schema'
import type {
  ClientVouchers,
  ClientVouchersData,
  ClientVouchersPatch,
  ClientVouchersQuery,
} from '../types/typebox/vouchers.schema'
import type {
  ClientVendors,
  ClientVendorsData,
  ClientVendorsPatch,
  ClientVendorsQuery,
} from '../types/typebox/vendors.schema'
import type {
  ClientVendorProductMappings,
  ClientVendorProductMappingsData,
  ClientVendorProductMappingsPatch,
  ClientVendorProductMappingsQuery,
} from '../types/typebox/vendor-product-mappings.schema'
import type {
  ClientInvoiceProcessingLogs,
  ClientInvoiceProcessingLogsData,
  ClientInvoiceProcessingLogsQuery,
} from '../types/typebox/invoice-processing-logs.schema'
import type {
  ClientLogs,
  ClientLogsData,
  ClientLogsPatch,
  ClientLogsQuery,
} from '../types/typebox/logs.schema'
import {
  useServiceStoreFactory,
  type FeathersQuery,
  type Params as FeathersParams,
} from './useServiceStoreFactory'

export type ServiceQueries = {
  products: Omit<ClientProductsQuery, '$select' | '$limit' | '$skip'>
  apps: Omit<ClientAppsQuery, '$select' | '$limit' | '$skip'>
  users: Omit<ClientUsersQuery, '$select' | '$limit' | '$skip'>
  storefronts: Omit<ClientStorefrontsQuery, '$select' | '$limit' | '$skip'>
}

const menuItemsCustomMethods = ['publishVersion'] as const
const logsCustomMethods = ['recover'] as const
const ordersCustomMethods = ['applyPromotion', 'removePromotion'] as const

export default function useFeathersStore<
  ServiceName extends string,
  Result,
  Data,
  PatchData,
  Query extends FeathersQuery,
  Extend extends readonly string[] = [],
>({
  api,
  serviceName,
  idField,
  modelDefaults,
  options,
}: {
  api: Application
  serviceName: ServiceName
  idField: '_id'
  modelDefaults: () => Data
  options?: { extendStore?: Extend }
}) {
  const service = api.service(serviceName)
  if (options?.extendStore?.length) {
    // @ts-expect-error - methods is dynamically added
    api.service(serviceName).methods(...options.extendStore)
  }

  const useStore = useServiceStoreFactory<
    Result,
    Data,
    PatchData,
    Query,
    Extend
  >(
    service,
    serviceName,
    idField,
    modelDefaults,
    // @ts-expect-error - extendStore is dynamically typed
    options?.extendStore || [],
  )

  return useStore()
}

export const serviceStores = (api: any) => {
  return {
    products: useFeathersStore<
      'products',
      ClientProducts,
      ClientProductsData,
      ClientProductsPatch,
      ClientProductsQuery,
      []
    >({
      api: api,
      serviceName: 'products',
      idField: '_id',
      modelDefaults: () => {
        return {
          name: 'New Item',
          unitOfMeasure: 'gr',
          type: 'ingredient',
          normalStockLevel: 0,
          averageExpirationTime: 0,
          isAllergic: false,
        }
      },
    }),
    apps: useFeathersStore<
      'apps',
      ClientApps,
      ClientAppsData,
      ClientAppsPatch,
      ClientAppsQuery,
      []
    >({
      api: api,
      serviceName: 'apps',
      idField: '_id',
      modelDefaults: () => {
        return {
          name: 'New App',
          taxes: [],
          menuLanguages: [
            {
              _id: nanoid(),
              code: 'en-gb',
              name: 'English',
              fallback: true,
            },
          ],
          currency: {
            sign: '€',
            code: 'euro',
            name: 'euro',
          },
          currencies: [],
        }
      },
    }),
    users: useFeathersStore<
      'users',
      ClientUsers,
      ClientUsersData,
      ClientUsersPatch,
      ClientUsersQuery,
      []
    >({
      api: api,
      serviceName: 'users',
      idField: '_id',
      modelDefaults: () => {
        return {
          email: '',
          password: '',
          status: 'public',
          profile: {
            useShifts: false,
            role: 'waiter',
            firstName: '',
            lastName: '',
          },
          permissions: {
            type: 'customer',
          },
        }
      },
    }),
    storefronts: useFeathersStore<
      'storefronts',
      ClientStorefronts,
      ClientStorefrontsData,
      ClientStorefrontsPatch,
      ClientStorefrontsQuery,
      []
    >({
      api: api,
      serviceName: 'storefronts',
      idField: '_id',
      modelDefaults: () => {
        return {
          name: 'New Storefront',
          inHouseService: {
            enabled: false,
            menuIds: [],
            fees: [],
            storefrontPlaceIds: [],
            setup: {
              batches: false,
              map: false,
              tabs: false,
              cashier: false,
              shifts: false,
            },
            unitMapping: {
              categories: {},
              menuItems: {},
            },
            storefrontPlaceAreas: [],
          },
          inHouseDelivery: {
            enabled: false,
            menuIds: [],
            fees: [],
            storefrontPlaceIds: [],
            map: false,
            unitMapping: {
              categories: {},
              menuItems: {},
            },
            storefrontPlaceAreas: [],
          },
          delivery: {
            enabled: false,
            menuIds: [],
            fees: [],
            map: null,
            unitMapping: {
              categories: {},
              menuItems: {},
            },
          },
          pickup: {
            enabled: false,
            menuIds: [],
            fees: [],
            unitMapping: {
              categories: {},
              menuItems: {},
            },
          },
          units: [],
        }
      },
    }),
    goods: useFeathersStore<
      'goods',
      ClientGoods,
      ClientGoodsData,
      ClientGoodsPatch,
      ClientGoodsQuery,
      []
    >({
      api: api,
      serviceName: 'goods',
      idField: '_id',
      modelDefaults: () => {
        return {
          exitAmount: 0,
          name: '',
          averageExpirationTime: 0,
          unitOfMeasure: 'gr',
          isAllergic: false,
          products: [],
          normalStockLevel: 0,
        }
      },
    }),
    transactions: useFeathersStore<
      'transactions',
      ClientTransactions,
      ClientTransactionsData,
      ClientTransactionsPatch,
      ClientTransactionsQuery,
      []
    >({
      api: api,
      serviceName: 'transactions',
      idField: '_id',
      modelDefaults: () => {
        return {
          paid: false,
          orderId: '',
          items: [],
          direction: 'incoming',
          type: 'card',
        }
      },
    }),
    'stock-transactions': useFeathersStore<
      'stock-transactions',
      ClientStockTransactions,
      ClientStockTransactionsData,
      ClientStockTransactionsPatch,
      ClientStockTransactionsQuery,
      []
    >({
      api: api,
      serviceName: 'stock-transactions',
      idField: '_id',
      modelDefaults: () => {
        return {
          type: 'write-off',
          products: {},
        }
      },
    }),
    'menu-items': useFeathersStore<
      'menu-items',
      ClientMenuItems,
      ClientMenuItemsData,
      ClientMenuItemsPatch,
      ClientMenuItemsQuery,
      typeof menuItemsCustomMethods
    >({
      api: api,
      serviceName: 'menu-items',
      idField: '_id',
      modelDefaults: () => {
        return {
          internalName: '',
          taxId: '',
          status: 'draft',
          name: [],
          description: [],
          products: [],
          variations: [],
          unitOfMeasure: 'gr',
          amount: 0,
          extras: [],
          consumerPrice: 0,
          cookingTime: 0,
        }
      },
      options: {
        extendStore: menuItemsCustomMethods,
      },
    }),
    orders: useFeathersStore<
      'orders',
      ClientOrders,
      ClientOrdersData,
      ClientOrdersPatch,
      ClientOrdersQuery,
      typeof ordersCustomMethods
    >({
      api: api,
      serviceName: 'orders',
      idField: '_id',
      modelDefaults: () => {
        return {
          staff: [],
          status: {
            name: 'created',
            createdAt: new Date().getTime(),
          },
          menuItems: [],
          batches: [],
          publicId: '',
          tabName: '',
          fees: [],
          type: 'inHouseService',
          storefrontPlaceIds: [],
        }
      },
      options: {
        extendStore: ordersCustomMethods,
      },
    }),
    'storefront-places': useFeathersStore<
      'storefront-places',
      ClientStorefrontPlaces,
      ClientStorefrontPlacesData,
      ClientStorefrontPlacesPatch,
      ClientStorefrontPlacesQuery,
      []
    >({
      api: api,
      serviceName: 'storefront-places',
      idField: '_id',
      modelDefaults: () => {
        return {
          _id: nanoid(),
          storefrontAreaId: '',
          storefrontId: '',
          enabled: true,
          orderType: 'inHouseService',
          coords: [],
          config: {
            type: 'table',
            name: '1',
            round: false,
            shape: 'rectangle',
            seating: [1, 2, 1, 2],
            rotation: 0,
          },
        }
      },
    }),
    menus: useFeathersStore<
      'menus',
      ClientMenus,
      ClientMenusData,
      ClientMenusPatch,
      ClientMenusQuery,
      []
    >({
      api: api,
      serviceName: 'menus',
      idField: '_id',
      modelDefaults: () => {
        return {
          status: 'hidden',
          name: [],
          categories: [],
          menuItems: [],
        }
      },
    }),
    promotions: useFeathersStore<
      'promotions',
      ClientPromotions,
      ClientPromotionsData,
      ClientPromotionsPatch,
      ClientPromotionsQuery,
      []
    >({
      api: api,
      serviceName: 'promotions',
      idField: '_id',
      modelDefaults: () => {
        return {
          status: 'draft',
          name: '',
          description: '',
          startDate: new Date().getTime(),
          endDate: new Date().getTime(),
          orderType: 'inHouseService',
          combinable: true,
          activation: [],
          discount: {
            type: 'percentage',
            value: 0,
          },
        }
      },
    }),
    shifts: useFeathersStore<
      'shifts',
      ClientShifts,
      ClientShiftsData,
      ClientShiftsPatch,
      ClientShiftsQuery,
      []
    >({
      api: api,
      serviceName: 'shifts',
      idField: '_id',
      modelDefaults: () => {
        return {
          startsAt: new Date().getTime(),
          endsAt: new Date().getTime(),
          assignedStaffIds: [],
          staffLogs: {},
        }
      },
    }),
    'shift-templates': useFeathersStore<
      'shift-templates',
      ClientShiftTemplates,
      ClientShiftTemplatesData,
      ClientShiftTemplatesPatch,
      ClientShiftTemplatesQuery,
      []
    >({
      api: api,
      serviceName: 'shift-templates',
      idField: '_id',
      modelDefaults: () => {
        return {
          name: '',
          description: '',
          startTime: 0,
          endTime: 0,
          scheduleType: 'daysOfWeek',
          daysOfWeek: [],
          staffByRole: {},
        }
      },
    }),
    vouchers: useFeathersStore<
      'vouchers',
      ClientVouchers,
      ClientVouchersData,
      ClientVouchersPatch,
      ClientVouchersQuery
    >({
      api: api,
      serviceName: 'vouchers',
      idField: '_id',
      modelDefaults: () => {
        return {
          appId: '',
          promotionId: '',
          code: '',
          used: false,
          batch: false,
        }
      },
    }),
    vendors: useFeathersStore<
      'vendors',
      ClientVendors,
      ClientVendorsData,
      ClientVendorsPatch,
      ClientVendorsQuery,
      []
    >({
      api: api,
      serviceName: 'vendors',
      idField: '_id',
      modelDefaults: () => {
        return {
          name: '',
        }
      },
    }),
    'vendor-product-mappings': useFeathersStore<
      'vendor-product-mappings',
      ClientVendorProductMappings,
      ClientVendorProductMappingsData,
      ClientVendorProductMappingsPatch,
      ClientVendorProductMappingsQuery,
      []
    >({
      api: api,
      serviceName: 'vendor-product-mappings',
      idField: '_id',
      modelDefaults: () => {
        return {
          vendorId: '',
          rawText: '',
          rawTextNormalized: '',
          productId: '',
          confirmedByUser: false,
          timesSeen: 1,
          lastSeenAt: new Date().getTime(),
        }
      },
    }),
    'invoice-processing-logs': useFeathersStore<
      'invoice-processing-logs',
      ClientInvoiceProcessingLogs,
      ClientInvoiceProcessingLogsData,
      ClientInvoiceProcessingLogsData,
      ClientInvoiceProcessingLogsQuery,
      []
    >({
      api: api,
      serviceName: 'invoice-processing-logs',
      idField: '_id',
      modelDefaults: () => {
        return {
          llmModelUsed: '',
          llmTokensInput: 0,
          llmTokensOutput: 0,
          llmCostUsd: 0,
          processingTimeMs: 0,
          rawRequest: null,
          rawResponse: null,
        }
      },
    }),
  }
}

type ProcurementStore = ReturnType<
  typeof useFeathersStore<
    'procurement',
    ClientProcurement,
    ClientProcurementData,
    ClientProcurementPatch,
    ClientProcurementQuery,
    []
  >
>

export const procurementServiceStores = (
  api: any,
): { procurement: ProcurementStore } => {
  return {
    procurement: useFeathersStore<
      'procurement',
      ClientProcurement,
      ClientProcurementData,
      ClientProcurementPatch,
      ClientProcurementQuery,
      []
    >({
      api: api,
      serviceName: 'procurement',
      idField: '_id',
      modelDefaults: () => {
        return {
          createdAt: new Date().getTime(),
          status: 'backlog',
          entry: [],
        }
      },
    }),
  }
}

export const logsServiceStores = (api: any) => {
  return {
    logs: useFeathersStore<
      'logs',
      ClientLogs,
      ClientLogsData,
      ClientLogsPatch,
      ClientLogsQuery,
      typeof logsCustomMethods
    >({
      api: api,
      serviceName: 'logs',
      idField: '_id',
      modelDefaults: () => {
        return {
          appId: '',
          docId: '',
          userId: '',
          service: 'orders',
          action: 'patch',
        }
      },
      options: {
        extendStore: logsCustomMethods,
      },
    }),
  }
}

export const staffPaymentServiceStores = (api: any) => {
  return {
    'staff-contracts': useFeathersStore<
      'staff-contracts',
      ClientStaffContracts,
      ClientStaffContractsData,
      ClientStaffContractsPatch,
      ClientStaffContractsQuery,
      []
    >({
      api: api,
      serviceName: 'staff-contracts',
      idField: '_id',
      modelDefaults: () => {
        return {
          userId: '',
          effectiveFrom: new Date().getTime(),
          status: 'draft',
          compensation: {
            kind: 'salary',
            baseRate: 0,
            period: 'month',
            currencyCode: '',
          },
        }
      },
    }),
    'staff-payroll': useFeathersStore<
      'staff-payroll',
      ClientStaffPayroll,
      ClientStaffPayrollData,
      ClientStaffPayrollPatch,
      ClientStaffPayrollQuery,
      []
    >({
      api: api,
      serviceName: 'staff-payroll',
      idField: '_id',
      modelDefaults: () => {
        const now = new Date()
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
        const endOfMonth = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0,
          23,
          59,
          59,
          999,
        )
        return {
          userId: '',
          contractId: '',
          forPeriodFrom: startOfMonth.getTime(),
          forPeriodTo: endOfMonth.getTime(),
          currencyCode: '',
          items: [],
          status: 'draft',
        }
      },
    }),
  }
}

export const taskServiceStores = (api: any) => {
  return {
    tasks: useFeathersStore<
      'tasks',
      ClientTasks,
      ClientTasksData,
      ClientTasksPatch,
      ClientTasksQuery,
      []
    >({
      api,
      serviceName: 'tasks',
      idField: '_id',
      modelDefaults: () => {
        return {
          isActive: true,
          activation: {
            kind: 'group' as const,
            logic: 'and' as const,
            children: [],
          },
          assignedStaffIds: [],
          title: '',
          acceptanceCriteria: [],
        }
      },
    }),
    'task-logs': useFeathersStore<
      'task-logs',
      ClientTaskLogs,
      ClientTaskLogsData,
      ClientTaskLogsPatch,
      ClientTaskLogsQuery,
      []
    >({
      api,
      serviceName: 'task-logs',
      idField: '_id',
      modelDefaults: () => {
        return {
          taskId: '',
          staffId: '',
          type: 'started',
          triggered: { date: '' },
          matchedRules: [],
        }
      },
    }),
  }
}

export type Params = FeathersParams
export type ServiceStores = ReturnType<typeof serviceStores> &
  ReturnType<typeof logsServiceStores> &
  ReturnType<typeof staffPaymentServiceStores> &
  ReturnType<typeof taskServiceStores> &
  ReturnType<typeof procurementServiceStores>
export type ServiceStore = ServiceStores[keyof ServiceStores]
export type ServiceStoreKey = keyof ServiceStores

// Re-export error types and utilities for convenience
export type {
  ServiceError,
  ValidationError,
  BadRequestError,
  NotFoundError,
  NotAuthenticatedError,
  ForbiddenError,
  ConflictError,
  UnprocessableError,
  GeneralError,
} from './types/errors'
export {
  isServiceError,
  isValidationError,
  isBadRequestError,
  isNotFoundError,
  isNotAuthenticatedError,
  isForbiddenError,
  isConflictError,
  isUnprocessableError,
  isGeneralError,
  isClientError,
  isServerError,
  getValidationMessages,
  getErrorMessage,
} from './types/errors'

// Re-export error handling utilities
export {
  asServiceError,
  handleServiceError,
  withServiceError,
  handleValidationError,
  catchAndNotify,
} from './utils/errorHandling'
